import { Button, Grid, makeStyles, TextField } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import {
  ActionButtonsContainer,
  CloseButton,
  Container,
  FieldsSection,
  FormContent,
  Header,
  HeaderTitle,
} from "./styles";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  createCompany,
  updateCompany,
} from "../../../../../services/companies.service";
import { ICompany } from "../../../../../types/companies";

interface ICreateCompanyModalProps {
  setModalStatus: (status: boolean) => void;
  businessId: string;
  onSuccess: () => void;
  setToastMessage: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      content: string;
      type: string;
      autoHideDuration: number;
    }>
  >;
  companyToEdit?: ICompany;
}

interface ICompanyData {
  name: string;
  abbreviation: string;
  corporateName?: string;
}

const useStyles = makeStyles((theme) => ({
  button: {
    ...theme.typography.subtitle1,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
  },
  buttonOutlined: {
    border: "1px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: "#666666",
    textTransform: "capitalize",
  },
}));

const schema = yup.object().shape({
  name: yup.string().required(),
  abbreviation: yup.string().max(3).required(),
  corporateName: yup.string(),
});

const CreateCompanyModal: React.FC<ICreateCompanyModalProps> = ({
  setModalStatus,
  setToastMessage,
  onSuccess,
  businessId,
  companyToEdit,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<ICompanyData>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (companyToEdit) {
      const { name, abbreviation, corporateName } = companyToEdit;
      setValue("name", name);
      setValue("abbreviation", abbreviation);
      setValue("corporateName", corporateName);
    }
  }, [companyToEdit]);

  const handleCreateCompany = async (data: ICompanyData) => {
    try {
      await createCompany({
        ...data,
        abbreviation: data.abbreviation.toUpperCase(),
        businessId: businessId,
      });

      setToastMessage({
        open: true,
        content: t(
          "softwareManagement.modules.business.successCompanyCreation"
        ),
        type: "success",
        autoHideDuration: 6000,
      });

      onSuccess();
    } catch (error: any) {
      setToastMessage({
        open: true,
        content:
          error?.response?.data?.message ||
          t("softwareManagement.modules.business.errorCompanyCreation"),
        type: "error",
        autoHideDuration: 6000,
      });
    } finally {
      setModalStatus(false);
    }
  };

  const handleUpdateCompany = async (data: ICompanyData) => {
    if (!companyToEdit) {
      setToastMessage({
        open: true,
        content: t("softwareManagement.modules.business.errorCompanyUpdate"),
        type: "error",
        autoHideDuration: 6000,
      });

      return;
    }

    try {
      await updateCompany(companyToEdit.id, {
        ...data,
        abbreviation: data.abbreviation.toUpperCase(),
        businessId: businessId,
      });

      setToastMessage({
        open: true,
        content: t("softwareManagement.modules.business.successCompanyUpdate"),
        type: "success",
        autoHideDuration: 6000,
      });

      onSuccess();
    } catch (error: any) {
      setToastMessage({
        open: true,
        content:
          error?.response?.data?.message ||
          t("softwareManagement.modules.business.errorCompanyUpdate"),
        type: "error",
        autoHideDuration: 6000,
      });
    } finally {
      setModalStatus(false);
    }
  };

  const onSubmit = async (data: ICompanyData) => {
    if (!companyToEdit) {
      handleCreateCompany(data);

      return;
    }

    handleUpdateCompany(data);
  };

  return (
    <Container>
      <Header>
        <HeaderTitle>{`${t(
          "softwareManagement.modules.business.createNewCompany"
        )}`}</HeaderTitle>

        <CloseButton onClick={() => setModalStatus(false)}>
          <Close />
        </CloseButton>
      </Header>

      <FormContent onSubmit={handleSubmit(onSubmit)}>
        <FieldsSection>
          <Grid container spacing={3}>
            <Grid item lg={6} xs={12}>
              <TextField
                autoComplete="nope"
                id="name"
                label={t("softwareManagement.name")}
                fullWidth
                required={true}
                error={!watch("name")}
                inputProps={{ maxLength: 16 }}
                {...register("name")}
                InputLabelProps={{ shrink: watch("name") ? true : false }}
              />
            </Grid>

            <Grid item lg={6} xs={12}>
              <TextField
                autoComplete="nope"
                id="abbreviation"
                label={t("softwareManagement.abbreviation")}
                fullWidth
                required={true}
                error={!watch("abbreviation")}
                inputProps={{ maxLength: 3 }}
                {...register("abbreviation")}
                InputLabelProps={{
                  shrink: watch("abbreviation") ? true : false,
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid item lg={6} xs={12}>
              <TextField
                autoComplete="nope"
                id="corporateName"
                label={t("softwareManagement.corporateName")}
                fullWidth
                {...register("corporateName")}
                InputLabelProps={{
                  shrink: watch("corporateName") ? true : false,
                }}
              />
            </Grid>
          </Grid>
        </FieldsSection>

        <ActionButtonsContainer>
          <Button
            variant="outlined"
            className={classes.buttonOutlined}
            onClick={() => setModalStatus(false)}
          >
            {t("cancel")}
          </Button>
          <Button variant="contained" className={classes.button} type="submit">
            {t("save")}
          </Button>
        </ActionButtonsContainer>
      </FormContent>
    </Container>
  );
};

export default CreateCompanyModal;
